import * as _ from 'lodash';
import { humanize } from '../../../shared/helpers/text.helper';

/*
  The types of event that the backend writes to the audit trail, in the words of the screen, by area. The API has no list of them (the
  filter by type is an exact match on the name: GET /admin/audit?eventType=TEAM_CREATED), so this is read from the code of the backend
  (the *AuditEventType enums, legaleyse-backend at cbe99c6) and has to follow it: a type that is not here is still shown in the table
  (humanised) and can still be chosen once it is in the filter, but it is not offered. Asked of the backend in
  legaleyse-frontend#83.
*/
export const auditEventTypeGroups = [
    {
        name: 'Alerts',
        types: [
            { value: 'ALERT_DISMISS', label: 'Alert dismissed' },
            { value: 'ALERT_DELEGATE', label: 'Alert delegated' },
            { value: 'ALERT_EXPORT_CSV', label: 'Alerts exported (CSV)' }
        ]
    },
    {
        name: 'Rules and risk',
        types: [
            { value: 'UPDATE_RULE', label: 'Rule changed' },
            { value: 'RISK_CONFIG_UPDATED', label: 'Risk weights changed' }
        ]
    },
    {
        name: 'Teams',
        types: [
            { value: 'TEAM_CREATED', label: 'Team created' },
            { value: 'TEAM_UPDATED', label: 'Team changed' },
            { value: 'TEAM_PERM_ADDED', label: 'Permission added to team' },
            {
                value: 'TEAM_PERM_REMOVED',
                label: 'Permission removed from team'
            },
            {
                value: 'TEAM_JUR_ADDED',
                label: 'Jurisdictions or brands added to team'
            },
            {
                value: 'TEAM_JUR_REMOVED',
                label: 'Jurisdictions or brands removed from team'
            },
            {
                value: 'TEAM_JUR_REPLACED',
                label: 'Jurisdictions of team replaced'
            }
        ]
    },
    {
        name: 'Users',
        types: [
            { value: 'USER_CREATED', label: 'User created' },
            { value: 'USER_UPDATED', label: 'User changed' },
            { value: 'USER_TEAM_ADDED', label: 'User added to team' },
            { value: 'USER_TEAM_REMOVED', label: 'User removed from team' }
        ]
    },
    {
        name: 'API users',
        types: [{ value: 'CLIENT_BRANDS_SET', label: 'API user brands set' }]
    },
    {
        name: 'Sandboxes',
        types: [
            { value: 'SIM_WS_CREATED', label: 'Sandbox created' },
            { value: 'SIM_WS_REFRESHED', label: 'Sandbox refreshed' },
            { value: 'SIM_WS_DELETED', label: 'Sandbox deleted' },
            { value: 'SIM_WORKSPACE_PURGE', label: 'Sandbox data cleared' },
            { value: 'SIM_RUN_CREATED', label: 'Simulation run created' }
        ]
    }
];

const knownLabels = _.fromPairs(
    _.flatMap(auditEventTypeGroups, group =>
        _.map(group.types, type => [type.value, type.label])
    )
);

/*
  What an event is about (its entityType), where the words of the screen are not the name made readable: the holder of a credential that
  sends events is an "API user", not a "client" (legaleyse-backend#121, legaleyse-frontend#77). The others are humanised.
*/
const entityTypeLabels = { CLIENT_CREDENTIAL: 'API user credential' };

export const entityTypeLabel = entityType =>
    entityTypeLabels[entityType] || humanize(entityType);

/* The value of "no type in particular" in the filter. */
export const ALL_TYPES = '';

/* A type in words: those that are known as the screen says them, and the others humanised ("SOME_NEW_TYPE" -> "Some new type"). */
export const eventTypeLabel = eventType =>
    knownLabels[eventType] || humanize(eventType);

/*
  The options of the filter by type, for react-dropdown: all types first, then each area with its types. A type that is being filtered by
  and is not in the list (the backend has a newer one) is added under "Other", so that the filter shows what it is doing.
*/
export const makeEventTypeOptions = current => {
    const unknown = current && !knownLabels[current] ? [current] : [];

    return [
        { value: ALL_TYPES, label: 'All types' },
        ..._.map(auditEventTypeGroups, group => ({
            type: 'group',
            name: group.name,
            items: group.types
        })),
        ...(unknown.length
            ? [
                  {
                      type: 'group',
                      name: 'Other',
                      items: _.map(unknown, value => ({
                          value,
                          label: eventTypeLabel(value)
                      }))
                  }
              ]
            : [])
    ];
};

/* The option that is chosen, for the value of the dropdown. */
export const findEventTypeOption = (options, value) =>
    _.find(
        _.flatMap(options, option => option.items || [option]),
        { value: value || ALL_TYPES }
    );
